<template>
  <FormSheet
    v-model:open="isOpen"
    :title="$t('Добавить Face ID')"
    :subtitle="$t('Регистрация биометрии для пользователя')"
    :width="560"
    @close="onClose"
  >
    <div class="flex flex-col gap-6">
      <section>
        <label class="mb-1 text-sm font-medium text-foreground">
          {{ $t("Пользователь") }}
        </label>
        <p class="mb-3 leading-5 text-gray-400">
          {{ $t("Выберите сотрудника компании для регистрации Face ID.") }}
        </p>
        <div class="relative" ref="pickerRef">
          <button
            type="button"
            class="flex h-11 w-full items-center gap-3 rounded-md border border-input bg-background px-3 text-left transition-colors hover:border-foreground/30"
            :class="pickerOpen ? 'border-ring ring-2 ring-ring/10' : ''"
            @click="pickerOpen = !pickerOpen"
          >
            <template v-if="selectedUser">
              <UserAvatar
                :identifier="selectedUser.identifier"
                :seed="selectedUser.uuid || selectedUser.id"
                :name="fullName(selectedUser)"
                :size="28"
              />
              <span class="flex-1 truncate text-sm font-medium text-foreground">
                {{ fullName(selectedUser) || "—" }}
              </span>
              <Tag
                v-if="selectedUser.role"
                variant="default"
                size="sm"
                class="rounded-md"
              >
                {{ selectedUser.role }}
              </Tag>
            </template>
            <template v-else>
              <svg-icon name="huge-user" class="h-4 w-4 text-gray-400" />
              <span class="flex-1 text-sm text-gray-400">
                {{ $t("Выбрать пользователя") }}
              </span>
            </template>
            <svg-icon
              name="huge-shevron-down"
              class="h-4 w-4 shrink-0 text-gray-400 transition-transform"
              :class="pickerOpen ? 'rotate-180' : ''"
            />
          </button>

          <div
            v-if="pickerOpen"
            class="absolute left-0 right-0 top-[calc(100%+6px)] z-10 flex max-h-80 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-lg"
          >
            <div class="border-b border-border p-2">
              <Input
                v-model="pickerQuery"
                class="h-8 text-sm"
                @input="onPickerSearch"
              />
            </div>
            <div class="flex-1 overflow-y-auto p-1">
              <div
                v-if="pickerLoading"
                class="px-3 py-6 text-center text-xs text-gray-400"
              >
                {{ $t("Загрузка") }}...
              </div>
              <div
                v-else-if="!candidateUsers.length"
                class="px-3 py-6 text-center text-xs text-gray-400"
              >
                {{ $t("Ничего не найдено") }}
              </div>
              <button
                v-for="u in candidateUsers"
                v-else
                :key="u.uuid || u.id"
                type="button"
                :disabled="isEnrolled(u)"
                :class="[
                  'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors',
                  isEnrolled(u)
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer hover:bg-muted',
                ]"
                @click="pickUser(u)"
              >
                <UserAvatar
                  :identifier="u.identifier"
                  :seed="u.uuid || u.id"
                  :name="fullName(u)"
                  :size="28"
                />
                <span
                  class="flex-1 truncate text-sm font-medium text-foreground"
                >
                  {{ fullName(u) }}
                </span>
                <Tag
                  :variant="isEnrolled(u) ? 'warning' : 'default'"
                  size="sm"
                  class="rounded-md"
                >
                  {{ isEnrolled(u) ? $t("Уже есть") : u.role || "—" }}
                </Tag>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section>
        <label class="mb-1 text-sm font-medium text-foreground">
          {{ $t("Фотографии") }}
        </label>
        <p class="mb-3 leading-5 text-gray-400">
          {{ $t("Минимум 3 фотографии. Хорошее освещение, лицо в кадре.") }}
        </p>
        <div class="grid grid-cols-2 gap-2.5">
          <div v-for="(slot, idx) in photos" :key="idx" class="relative">
            <input
              :ref="(el) => (fileRefs[idx] = el)"
              type="file"
              accept="image/*"
              class="hidden"
              @change="onFileSelected(idx, $event)"
            />
            <FacePhotoCell
              :preview-src="slot?.preview || ''"
              :number="idx + 1"
              :empty-label="slotLabels[idx]"
              :empty-hint="$t('JPG, PNG · до 5МБ')"
              :required="idx < 3"
              :clickable="!slot"
              show-checkmark
              @click-empty="pickFile(idx)"
            >
              <template v-if="slot" #actions>
                <button
                  type="button"
                  class="inline-flex h-6 w-6 items-center justify-center rounded-md bg-black/60 text-white transition-colors hover:bg-red-500"
                  @click.stop="removePhoto(idx)"
                >
                  <svg-icon name="huge-danger" class="h-3 w-3" />
                </button>
              </template>
            </FacePhotoCell>
          </div>
        </div>
        <div
          class="mt-3 flex items-center gap-2 leading-5"
          :class="filledCount >= 3 ? 'text-green-600' : 'text-gray-400'"
        >
          <svg-icon
            :name="filledCount >= 3 ? 'huge-check' : 'huge-warning'"
            class="h-4 w-4"
          />
          <span>
            {{ $t("Загружено") }} {{ filledCount }} {{ $t("из") }} 4<template
              v-if="filledCount < 3"
              >. {{ $t("Добавьте ещё") }} {{ 3 - filledCount }}</template
            >
          </span>
        </div>
      </section>
    </div>

    <template #footer>
      <div class="flex w-full items-center gap-2">
        <Button variant="ghost" @click="close">
          {{ $t("Отмена") }}
        </Button>
        <div class="flex-1" />
        <Button variant="default" :disabled="!canSave || saving" @click="save">
          <svg-icon name="huge-check" class="h-4 w-4" />
          {{ saving ? $t("Создание...") : $t("Создать Face ID") }}
        </Button>
      </div>
    </template>
  </FormSheet>
</template>

<script>
import { FormSheet } from "@/shared/ui/form-sheet";
import { Input } from "@/shared/ui/input";
import { Tag } from "@/shared/ui/tag";
import Button from "@/shared/ui/button/Button.vue";
import UserAvatar from "@/pages/admin/settings/tabs/face-id/UserAvatar.vue";
import FacePhotoCell from "@/components/face_recognition/FacePhotoCell.vue";
import { fullName } from "./utils";
import { getUserList } from "@/entities/user";
import { enrollPerson } from "@/entities/face-recognition";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default {
  name: "FaceIdCreateDrawer",
  components: { FormSheet, Input, Tag, Button, UserAvatar, FacePhotoCell },
  props: {
    open: { type: Boolean, default: false },
    enrolledIds: { type: Set, default: () => new Set() },
  },
  emits: ["update:open", "saved"],
  data() {
    return {
      pickerOpen: false,
      pickerQuery: "",
      pickerLoading: false,
      candidateUsers: [],
      searchTimer: null,
      selectedUser: null,
      photos: [null, null, null, null],
      fileRefs: [],
      saving: false,
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.self_user?.role === "ADMIN";
    },
    isOpen: {
      get() {
        return this.open;
      },
      set(v) {
        this.$emit("update:open", v);
      },
    },
    slotLabels() {
      return [
        this.$t("Анфас"),
        this.$t("Поворот влево"),
        this.$t("Поворот вправо"),
        this.$t("С улыбкой"),
      ];
    },
    filledCount() {
      return this.photos.filter(Boolean).length;
    },
    canSave() {
      return !!this.selectedUser && this.filledCount >= 0;
    },
  },
  watch: {
    open(value) {
      if (value) {
        this.reset();
        this.fetchCandidates();
      }
    },
    pickerOpen(value) {
      if (value) {
        document.addEventListener("mousedown", this.onDocumentClick);
      } else {
        document.removeEventListener("mousedown", this.onDocumentClick);
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener("mousedown", this.onDocumentClick);
    this.cleanupPreviews();
  },
  methods: {
    fullName,
    reset() {
      this.cleanupPreviews();
      this.pickerOpen = false;
      this.pickerQuery = "";
      this.selectedUser = null;
      this.photos = [null, null, null, null];
      this.saving = false;
    },
    cleanupPreviews() {
      this.photos.forEach((p) => {
        if (p?.preview) URL.revokeObjectURL(p.preview);
      });
    },
    close() {
      this.isOpen = false;
    },
    onClose() {
      this.reset();
    },
    onDocumentClick(e) {
      const root = this.$refs.pickerRef;
      if (root && !root.contains(e.target)) this.pickerOpen = false;
    },
    async fetchCandidates() {
      this.pickerLoading = true;
      try {
        const params = {
          page: 1,
          page_size: 50,
        };
        const q = this.pickerQuery.trim().toLowerCase();
        if (q) params.q = q;
        const data = await getUserList(params);
        if (data?.success && data?.payload) {
          this.candidateUsers = data.payload.items || [];
        }
      } finally {
        this.pickerLoading = false;
      }
    },
    onPickerSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.fetchCandidates(), 300);
    },
    isEnrolled(user) {
      const pid = String(user.uuid || user.id || "");
      return pid ? this.enrolledIds.has(pid) : false;
    },
    pickUser(user) {
      if (this.isEnrolled(user)) return;
      this.selectedUser = user;
      this.pickerOpen = false;
    },
    pickFile(idx) {
      this.fileRefs[idx]?.click();
    },
    onFileSelected(idx, event) {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;
      if (!file.type.startsWith("image/")) return;
      if (file.size > MAX_FILE_SIZE) return;
      const old = this.photos[idx];
      if (old?.preview) URL.revokeObjectURL(old.preview);
      const next = [...this.photos];
      next[idx] = { file, preview: URL.createObjectURL(file) };
      this.photos = next;
    },
    removePhoto(idx) {
      const old = this.photos[idx];
      if (old?.preview) URL.revokeObjectURL(old.preview);
      const next = [...this.photos];
      next[idx] = null;
      this.photos = next;
    },
    async save() {
      if (!this.isAdmin) return;
      if (!this.canSave || this.saving) return;
      this.saving = true;
      try {
        const files = this.photos.filter(Boolean).map((p) => p.file);
        const pid = String(
          this.selectedUser.uuid || this.selectedUser.id || ""
        );
        await enrollPerson({
          personId: pid,
          personName: fullName(this.selectedUser),
          files,
        });
        this.$emit("saved", {
          user: this.selectedUser,
          photoCount: files.length,
        });
        this.isOpen = false;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
